<!-- src/components/FooterNav.vue -->
<template>
  <!-- Show login/register links when not authenticated -->
  <div v-if="!authStore.isAuthenticated" class="footer-nav position-fixed">
    <div class="access-account">
      <RouterLink class="register" :to="{ name: 'register' }">register</RouterLink>
      <RouterLink class="login" :to="{ name: 'login' }">login</RouterLink>
    </div>
  </div>

  <!-- Show full navigation when authenticated -->
  <div v-else class="footer-nav position-fixed">
    <!-- top summary bar -->
    <div class="betslip-title-wrap" id="betslipDetails" v-if="betSlipStore.hasBets">
      <div class="container">
        <div class="row">
          <div class="col-4">
            <div class="betslip-stake-info">
              <span class="betslip-no">Stake:</span>
              <span class="betslip-info">{{ betSlipStore.stake.toFixed(2) }}</span>
            </div>
          </div>
          <div class="col-4">
            <div class="text-center">
              <span class="betslip-no">Odds:</span>
              <span class="betslip-info">{{ betSlipStore.totalOdds }}</span>
            </div>
          </div>
          <div class="col-4 text-right">
            <div class="possible-payout">
              <span class="possible-payout-text">Payout:</span>
              <span class="possible-payout-amount">
                <span class="payout-amount">{{ finalPayoutAmount.toFixed(2) }}</span>
                <span class="currency">KES</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dynamic boosted amount section - only show when there are bets -->
    <div id="boosted_amount" v-if="showBoostedNotice">
      <div class="container">
        <div class="row">
          <div class="col-12 text-left d-flex">
            <span class="text">
              <RouterLink to="/promotion"><i class="bi bi-info-circle-fill"></i></RouterLink>
            </span>
            <p v-if="gamesToAdd > 0">
              Add <span class="add-game-number">{{ gamesToAdd }}</span>
              more selections &amp; get {{ nextBoost.nextBonus }}% Multiboost
            </p>
            <p v-else-if="allOddsAbove13 && qualifyingBetsCount >= 4 && nextBoost" class="text-center">
              Add {{ nextBoost.nextLegsNeeded }} more to get a {{ nextBoost.nextBonus }}% boost!
            </p>
            <p v-else-if="qualifyingBetsCount > 0 && !allOddsAbove13" class="text-center">
              Add {{ nextBoost.nextLegsNeeded }} more to get a {{ nextBoost.nextBonus }}% boost!
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- bottom nav -->
    <div class="footer-content-wrap">
      <div class="container">
        <div class="row">
          <div class="col-12 px-0">
            <div class="footer-content">
              <div class="page-nav">
                <RouterLink class="active-page" id="menu" to="/">
                  <i class="fi fi-rr-home"></i>
                  <span class="text">Home</span>
                </RouterLink>
              </div>

              <div class="page-nav">
                <RouterLink to="/live-games" id="live" style="color: #cf2029">
                  <span class="live-indicator">10</span>
                  <i class="fi fi-rr-play-circle"></i>
                  <span class="text" style="color: #cf2029">Live</span>
                </RouterLink>
              </div>

              <div class="page-nav" @click="app.toggleBetSlip">
                <a href="javascript:void(0)" id="betslip">
                  <i class="fi fi-rr-add-document"></i>
                  <span class="text">Bet Slip</span>
                  <div id="betslipTicket" v-if="betSlipStore.betCount > 0">
                    <div class="betslip-ticket">{{ betSlipStore.betCount }}</div>
                  </div>
                </a>
              </div>

              <div class="page-nav">
                <RouterLink to="/my-bets" id="mybets">
                  <i class="fi fi-rr-document"></i>
                  <span class="text">my Bets</span>
                </RouterLink>
              </div>

              <div class="page-nav">
                <RouterLink to="/my-account" id="my-account">
                  <i class="fi fi-rr-user"></i>
                  <span class="text">Account</span>
                </RouterLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useAppStore } from '@/stores/app'
import { useBetSlipStore } from '@/stores/betSlipStore'
import { useAuthStore } from '@/stores/auth'

const app = useAppStore()
const betSlipStore = useBetSlipStore()
const authStore = useAuthStore()

// Prevent body scrolling when betslip is open on mobile
watch(
  () => app.betSlipOpen,
  (isOpen) => {
    if (window.innerWidth < 768) {
      document.body.style.overflow = isOpen ? 'hidden' : 'auto'
    }
  },
)

// Boost table for multiboost calculations
const BOOST_TABLE = [
  { legs: 4, bonus: 4 }, { legs: 5, bonus: 5 }, { legs: 6, bonus: 6 }, { legs: 7, bonus: 8 },
  { legs: 8, bonus: 10 }, { legs: 9, bonus: 15 }, { legs: 10, bonus: 18 }, { legs: 11, bonus: 30 },
  { legs: 12, bonus: 35 }, { legs: 13, bonus: 40 }, { legs: 14, bonus: 45 }, { legs: 15, bonus: 50 },
  { legs: 16, bonus: 55 }, { legs: 17, bonus: 60 }, { legs: 18, bonus: 65 }, { legs: 19, bonus: 70 },
  { legs: 20, bonus: 75 }, { legs: 21, bonus: 80 }, { legs: 22, bonus: 85 }, { legs: 23, bonus: 90 },
  { legs: 24, bonus: 95 }, { legs: 25, bonus: 100 }, { legs: 26, bonus: 100 },
]

function getNextBonusInfo(numLegs) {
  if (numLegs >= 26) return null
  for (const entry of BOOST_TABLE) {
    if (entry.legs > numLegs) {
      return {
        nextLegsNeeded: entry.legs - numLegs,
        nextBonus: entry.bonus,
      }
    }
  }
  return null
}

// Show boosted notice when there are bets
const showBoostedNotice = computed(() => betSlipStore.betCount > 0)

// Check if every selected odd >= 1.3 (qualifying odds for multiboost)
const allOddsAbove13 = computed(() => {
  if (!betSlipStore.bets.length) return false
  return betSlipStore.bets.every((b) => parseFloat(b?.odds) >= 1.3)
})

// Count how many legs have odds >= 1.3 (qualifying bets)
const qualifyingBetsCount = computed(() =>
  betSlipStore.bets.filter((b) => parseFloat(b?.odds) >= 1.3).length,
)

// How many more qualifying selections needed to reach 4
const gamesToAdd = computed(() =>
  qualifyingBetsCount.value >= 4 ? 0 : 4 - qualifyingBetsCount.value,
)

// Get next bonus info based on qualifying bets count
const nextBoost = computed(() => getNextBonusInfo(qualifyingBetsCount.value))

// Calculate final payout amount (similar to BetSlip component)
const finalPayoutAmount = computed(() => {
  if (!betSlipStore.hasBets) return 0

  const taxData = betSlipStore.calculateTaxesAndWinnings(
    betSlipStore.stake,
    parseFloat(betSlipStore.totalOdds),
  )

  return parseFloat(taxData.payout.toFixed(2))
})
</script>
